<template>
    <div class="page">
        <div class="login-popup backdrop-blur elevation-12">
            <div class="coa-syntax-headline coa-syntax-text">CEDEX ADMINISTRATION FACILITY</div>
            <div class="logo"></div>
            <div class="coa-syntax-description coa-syntax-text">{{ $t('Harmonization of CEDEX Code Interpretations') }}</div>
            <div class="iso-headline">ISO 9897</div>
            <div v-if="view === 'login'" class="login-form">
                <form @submit.prevent="login" class="">
                    <v-text-field
                        prepend-icon="mdi-account"
                        v-model="user"
                        name="user"
                        required
                        :placeholder="$t('Username')"
                        type="text"
                        autofocus
                        autocomplete="username"
                    />
                    <v-text-field
                        prepend-icon="mdi-lock"
                        v-model="password"
                        name="password"
                        required
                        :placeholder="$t('Password')"
                        type="password"
                        autocomplete="current-password"
                    />
                    <div class="button-pane">
                        <v-btn
                            class="button-prime w-3"
                            type="submit"
                        >{{ $t('Login') }}
                        </v-btn>
                        <v-btn
                            class="button-secondary w-3"
                            @click="redirect('forgotPassword')"
                        >{{ $t('Forgot password') }}
                        </v-btn>
                        <v-btn
                            class="button-secondary w-3"
                            @click="redirect('register')"
                        >{{ $t('Sign Up') }}
                        </v-btn>
                    </div>
                </form>
            </div>
            <div v-else-if="view === 'forgotPassword'" class="login-form">
                <form @submit.prevent="forgotPassword" class="">
                    <v-text-field
                        prepend-icon="mdi-email"
                        v-model="mail"
                        name="mail"
                        required
                        :placeholder="$t('E-Mail-Address')"
                        type="text"
                        autofocus
                    />
                    <div class="button-pane">
                        <v-btn
                            class="button-prime w-2"
                            type="submit"
                        >{{ $t('Send Password') }}
                        </v-btn>
                        <v-btn
                            class="button-secondary w-2"
                            @click="redirect('login')"
                        >{{ $t('Back') }}
                        </v-btn>
                    </div>
                </form>
            </div>
            <div v-else-if="view === 'register'" class="login-form">
                <form @submit.prevent="showTosPopup" class="">
                    <v-text-field
                        v-model="firstname"
                        name="firstname"
                        required
                        :label="$t('Firstname') + ' *'"
                        type="text"
                        autofocus
                        dense
                    />

                    <v-text-field
                        v-model="lastname"
                        name="lastname"
                        required
                        :label="$t('Lastname') + ' *'"
                        type="text"
                        dense
                    />

                    <v-text-field
                        v-model="company"
                        name="company"
                        required
                        :label="$t('Company / Organization') + ' *'"
                        type="text"
                        dense
                    />

                    <v-text-field
                        v-model="mail"
                        name="mail"
                        required
                        :label="$t('E-Mail-Address') + ' *'"
                        type="text"
                        dense
                    />

                    <div class="input-row mobile">
                        <vue-country-code
                            @onSelect="countryCodeSelect"
                            :preferredCountries="['us', 'cn', 'de', 'ee']"/>

                        <v-text-field
                            v-model="mobile"
                            name="mobile"
                            required
                            :label="$t('Mobile') + ' *'"
                            type="text"
                            dense
                            class="mobile"
                        />
                    </div>

                    <v-text-field
                        v-model="phone"
                        name="phone"
                        :label="$t('Phone')"
                        type="text"
                        dense
                    />

                    <div class="button-pane">
                        <v-btn
                            class="button-prime w-2"
                            type="submit"
                            :class="{disabled: signUpDisabled}"
                        >{{ $t('Sign Up') }}
                        </v-btn>
                        <v-btn
                            class="button-secondary w-2"
                            @click="redirect('login')"
                        >{{ $t('Back') }}
                        </v-btn>
                    </div>

                    <p class="text"> {{ $t('Button Sign Up will create an account on COA SYNTAX EDITOR.') }}</p>
                </form>
            </div>
            <div v-else-if="uuid !== ''" class="login-form">
                <form @submit.prevent="verify" class="">
                    <p class="text margin-top-16"> {{ $t('Please enter the verification code from the E-Mail you received!') }}</p>

                    <v-text-field
                        v-model="verificationCode"
                        name="verificationCode"
                        required
                        :label="$t('Verification Code') + ' *'"
                        type="text"
                        autofocus
                        dense
                    />

                    <div class="button-pane">
                        <v-btn
                            class="button-prime w-2"
                            type="submit"
                        >{{ $t('Verify') }}
                        </v-btn>
                        <v-btn
                            class="button-secondary w-2"
                            @click="redirect({name: 'Login'})"
                        >{{ $t('Home') }}
                        </v-btn>
                    </div>
                </form>
            </div>
            <div v-if="error" class="message error">{{ error }}</div>
            <div v-if="message" class="message success">{{ message }}</div>
        </div>
        <div v-if="showTos" class="tosBox flex-root-column">
            <div class="tosText flex-grow-1" ref="tosText" :class="{loading: registerLoading}">
                <TermsOfUse
                    :full-name="firstname + ' ' + lastname"
                    :company="company"
                />

                <div class="button-pane flex-grow-0">
                    <v-btn
                        class="button-prime w-3"
                        type="submit"
                        @click="register"
                        :disabled="registerLoading"
                    >{{ $t('Accept terms of use') }}
                    </v-btn>
                    <v-btn
                        class="button-secondary w-3"
                        @click="showTos = false"
                        :disabled="registerLoading"
                    >{{ $t('Decline') }}
                    </v-btn>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import Vue      from 'vue';
  import axios    from 'axios';
  import VueAxios from 'vue-axios';
  import router from "../router";
  import i18n from "../i18n";
  import TermsOfUse from "../components/TermsOfUse";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
Vue.use(VueAxios, axios);

export default {
    name: "LoginPage",
    components: {TermsOfUse},
    props: {
        view: {
            type:    String,
            default: 'login'
        },
        uuid: {
            type:    String,
            default: ''
        }
    },
    data() {
        return {
            user:             null,
            password:         null,

            mail:             null,

            firstname:        null,
            lastname:         null,
            company:          null,
            mobilePrefix:     '1',
            mobile:           null,
            phone:            null,
            showTos:          false,
            tosAccepted:      false,
            registerLoading:  false,

            verificationCode: null,

            error:            null,
            message:          null
        };
    },
    watch: {
        type() {
            this.error   = null;
            this.message = null;
        }
    },
    methods: {
        countryCodeSelect({dialCode}) {
            this.mobilePrefix = dialCode;
        },
        login() {
            let redirect = this.$auth.redirect();
            let data = new FormData();
            data.append('user', this.user);
            data.append('pwd', this.password);
            this.$auth.login({
                data,
                staySignedIn: true,
                redirect:     redirect ? redirect.from : {name: 'Root'},
                fetchUser:    false,
                fetchData:    false,
                error: (e) => {
                    // if (typeof a)
                    if (e.toString().indexOf('Request failed with status code 401') < 0) {
                        throw e;
                    }
                }
            })
                .catch(e => {
                    if (e.response.status === 401) {
                        this.error = i18n.t('User and password combination is unknown.')
                    } else if (e.response.status === 404) {
                        this.error = i18n.t('The target API endpoint is unreachable. Please contact and administrator.')
                    } else {
                        this.error = i18n.t('An unknown error occurred. Please try again later.')
                    }
                })

                // is done in auth.js => parseUserData
                .then(() => {
                    this.$auth.remember(JSON.stringify(this.$auth.user()));
                });


        },
        showTosPopup()
        {
            if (this.signUpDisabled) {
                return;
            }
            this.showTos = true;
        },
        redirect(item) {
            router.push(item);
        },
        forgotPassword() {
            let data = new FormData();
            data.append('email', this.mail);
            axios.post(
                'auth/regetLogin',
                data
            )
            .catch(e => {
                console.log(e);
                if (e.response.status < 200 && e.response.status > 299) {
                    this.message = null
                    this.error = i18n.t('An unknown error occurred. Please try again later.')
                }
            })

            // is done in auth.js => parseUserData
            .then(() => {
                this.message = i18n.t('If the E-Mail address is known, the system sent you a new password.');
                this.error = null;
            });
        },
        register() {
            this.registerLoading = true;
            let data = new FormData();
            let mobileNumber = '+' + this.mobilePrefix;
            if (this.mobile.charAt(0) === '0') {
                mobileNumber += this.mobile.substring(1);
            } else {
                mobileNumber += this.mobile;
            }
            data.append('firstName', this.firstname);
            data.append('lastName',  this.lastname);
            data.append('company',   this.company);
            data.append('mail',      this.mail);
            data.append('mobile',    mobileNumber);
            data.append('phone',     this.phone);
            data.append('systemUrl', location.href.replace('register', ''));

            let errorThrown = false;
            axios.post(
                'api/registerUser',
                data
            )
                .catch(e => {
                    let errorCode = 0;
                    if (e.response.status < 200 && e.response.status > 299) {
                        errorCode = e.response.status;
                        this.message = null
                        this.error = i18n.t('An unknown error occurred. Please try again later.');
                    } else {
                        errorCode = e.message.match(/\d+(.\d+)?/)[0];
                        this.message = '';
                        this.error   = i18n.t('Server is not reachable. Please retry at a later time.');
                    }

                    this.error += ' Error Code: ' + errorCode;
                })

                // is done in auth.js => parseUserData
                .then((response) => {
                    if (errorThrown) {
                        return;
                    }

                    this.showTos = false;
                    this.registerLoading = false;

                    if (response.data.success) {
                        this.message   = i18n.t('A new user was created. Please check your E-Mail inbox for further details.');
                        this.error     = null;

                        this.firstname = null;
                        this.mail      = null;
                        this.lastname  = null;
                        this.company   = null;
                        this.mobile    = null;
                        this.phone     = null;

                        this.redirect('login');
                    } else {
                        this.message = '';
                        this.error   = i18n.t(response.data.message ?? 'Server is not reachable. Please retry at a later time.');
                    }
                });
        },
        verify() {
            let data = new FormData();
            data.append('uuid',             this.uuid);
            data.append('verificationCode', this.verificationCode);

            axios.post(
                'api/verifyUser',
                data
            )
                .catch(e => {
                    console.log(e);
                    if (e.response.status < 200 && e.response.status > 299) {
                        this.message = null
                        this.error = i18n.t('An unknown error occurred. Please try again later.')
                    }
                })

                // is done in auth.js => parseUserData
                .then((response) => {
                    if (response.data.success) {
                        this.message = i18n.t('Your E-Mail address was verified. Your account will be activated. Please be patient.');
                        this.error   = null;

                        this.uuid             = null;
                        this.verificationCode = null;

                        this.redirect({name: 'Login'});
                    } else {
                        this.message = '';
                        this.error   = i18n.t(response.data.message);
                    }
                });
        }
    },
    computed: {
        signUpDisabled() {
            return (this.firstname || '').trim() === ''
                   || (this.lastname || '').trim() === ''
                   || (this.company || '').trim() === ''
                   || (this.mail || '').trim() === ''
                   || (this.mobile || '').trim() === ''
        }
    }
}
</script>

<style scoped>
.page {
    width: 100%;
    height: 100%;
    background-image: url("../assets/login_background.jpg");
    background-size: cover;
    background-attachment: fixed;
    background-position: bottom center;
}

/* slightly transparent fallback */
.backdrop-blur {
    background-color: rgb(255, 255, 255);
}

.margin-top-16 {
    margin-top: 16px;
}

/* if backdrop support: very transparent and blurred */
@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    .backdrop-blur {
        background-color: #eeeeeeb3;
        -webkit-backdrop-filter: blur(15px);
        backdrop-filter: blur(15px);
    }
}

.login-popup {
    position: absolute;
    top: 50%;
    left: 50%;
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    width: 30%;
    min-width: 600px;
    color: #666;
    text-align: center;

    padding: 32px;
    font-size: 15px;
    /*backdrop-filter: blur(15px);*/
}

div.logo {
    min-height: 80px;
    margin-top: 30px;
    background-image: url("../assets/coa_logo.png");
    background-repeat: no-repeat;
    background-position: center top;

}

.coa-syntax-text {
    color: #666;
    font-size: 18pt;
}

.coa-syntax-headline {
    text-transform: uppercase;
}

.coa-syntax-description {
    margin: 8px 0;
}

.iso-headline {
    color: #666;
    font-size: 13pt;
    margin-bottom: 24px;
}

.login-popup .login-form input[type="text"] {
    border-bottom: 1px solid var(--sc-blue-light);
    outline: none;
}

.login-popup .text {
    text-align: left;
}

button.w-3 {
    width: 30%;
}
button.w-2 {
    width: 48%;
}
button {
    opacity: 1;
    margin: 2%;
    border-radius: 0;
}
.login-popup .login-form button:first-child {
    margin-left: 0 !important;
}
.login-popup .login-form button:last-child {
    margin-right: 0;
}
.button-pane {
    margin-top: 2vh;
}
.login-popup .login-form button.button-prime[type="submit"],
.tosBox .button-pane button.button-prime[type="submit"] {
    background-color: var(--sc-blue);
    color: #fff;
    box-shadow: 1px 1px 6px grey;
}
.login-popup .login-form button.button-prime[type="submit"].disabled,
.tosBox .button-pane button.button-prime[type="submit"].disabled {
    background-color: grey;
}

.login-popup .login-form button.button-secondary,
.tosBox .button-pane button.button-secondary {
    background-color: #fff;
    color: var(--sc-blue);
    box-shadow: 1px 1px 6px grey;
}

.input-row > div {
    display: inline-block;
}
.input-row > div.v-input {
    width: 89%;
    margin-left: 8px;
}

div.message {
    padding: 8px;
    margin-top: 16px;
    color: white;
}

</style>
<style>
.theme--light.v-text-field > .v-input__control > .v-input__slot:before {
    border: none;
}

.login-popup .login-form .v-input.v-input--switch {
    margin-top: 0;
}

.login-popup .login-form .v-input:not(.v-input--switch) .v-input__slot {
    border-bottom: 1px solid var(--toolbar-select-underline-disabled);
}

.login-popup .login-form .v-text-field__details {
}

input:focus, textarea:focus {
    outline: none !important;
}

.tosBox {
    border: 2px solid var(--sc-blue);
    width: 70%;
    height: 80%;
    position: absolute;
    left: 15%;
    top: 10%;
    background-color: rgba(255,255,255,1);
}

.tosBox .tosText {
    overflow: auto;
}

.tosBox .tosText.loading {
    background-color: var(--sc-blue-light-background);
    color: var(--text-grey);
}
</style>